/**
 * The API client.
 *
 * tRPC over HTTP with superjson, which carries `bigint` natively — so a `Money`
 * crosses the wire as what it is and there is no string encoding to get wrong
 * on one side. The old tree already used superjson for `Date`; money is the
 * second thing that must not become a `number` in transit.
 */
import { QueryClient } from '@tanstack/react-query'
import { TRPCClientError, createTRPCClient, httpBatchLink } from '@trpc/client'
import superjson from 'superjson'
import type { AppRouter } from '@mgc/server/router'

/**
 * Where the server is. Baked at build time; the shell never supplies it.
 *
 * `||`, not `??`: the Dockerfile's `ARG VITE_API_URL=""` sets the variable to
 * the empty string rather than leaving it unset, and `??` would inline `''` —
 * a page whose every tRPC call goes to its own origin and 404s. The same
 * distinction is in `vite.config.ts`, which derives the CSP from this value.
 */
export const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) || 'http://localhost:4012'

let token: string | null = null
let staffToken: string | null = null

export function setToken(value: string | null): void {
  token = value
}

/**
 * Hold a staff authorisation for as long as an attendant is at the screen.
 *
 * This used to be `setStaff(staffId)` — a staff **id**, chosen here, sent as
 * `x-staff-id`, and believed by the server without any check. What travels now
 * is a token the server minted against a PIN it verified, bound to this device
 * and expiring on its own. The page cannot invent one.
 */
export function setStaffToken(value: string | null): void {
  staffToken = value
}

export const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_BASE}/trpc`,
      transformer: superjson,
      headers: () => ({
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
        ...(staffToken === null ? {} : { 'x-staff-token': staffToken })
      })
    })
  ]
})

/**
 * The refusal code the server attached, if it attached one.
 *
 * "Order failed" on a kiosk with a customer's card in a terminal is not an
 * error message, it is a support call. Every refusal the server can produce has
 * a name, and this is how the screen reads it.
 */
export function refusalOf(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) return null
  const data = error.data as { refusal?: unknown } | undefined
  return typeof data?.refusal === 'string' ? data.refusal : null
}

/**
 * What the server said, beyond the code — 1C's own sentence, usually.
 *
 * `refuse(code, detail)` puts this on the wire and until now nothing read it,
 * so «нөөц хүрэлцэхгүй» — the one line that names the actual problem — was
 * transmitted on every refusal and thrown away. A code alone tells a customer
 * *that* something is wrong; this is the half that says what.
 */
export function detailOf(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof TRPCClientError)) return null
  const data = error.data as { detail?: unknown } | undefined
  return typeof data?.detail === 'object' && data.detail !== null ? (data.detail as Record<string, unknown>) : null
}

/** The server's own words for a failure, if it gave any. For a log, and for a screen. */
export function reasonOf(error: unknown): string | null {
  const reason = detailOf(error)?.['reason']
  if (typeof reason === 'string' && reason.length > 0) return reason
  const message = error instanceof Error ? error.message : ''
  return message.length > 0 ? message : null
}

/**
 * A server that answered, and answered with something nobody planned for.
 *
 * The third case, and the one that used to be invisible: a 500, a thrown
 * `Error`, a validation failure — none of which carries a `refusal`, so
 * `refusalOf` returns null and the caller reports «offline». Telling a customer
 * the network is down when the server is up and broken sends them to a counter
 * with the wrong story and leaves nothing for anybody to debug.
 */
export function isServerFault(error: unknown): boolean {
  return error instanceof TRPCClientError && !isOffline(error) && refusalOf(error) === null
}

/**
 * The server answered, and it does not know this kiosk's token.
 *
 * Distinct from offline, and it has to be: the boot path used to fold both into
 * one `catch`, so a kiosk holding another deployment's token told the customer
 * the internet was down while it was talking to the server perfectly well.
 */
export function isUnenrolled(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = error.data as { code?: unknown } | undefined
  return data?.code === 'UNAUTHORIZED'
}

export function isOffline(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    // A fetch that never reached the server has no HTTP code attached.
    const data = error.data as { httpStatus?: number } | undefined
    return data?.httpStatus === undefined
  }
  return error instanceof TypeError
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A kiosk is a fixed panel nobody focuses or blurs, and a refetch storm on
      // reconnect is the last thing a store coming back online needs.
      refetchOnWindowFocus: false,
      retry: (count, error) => isOffline(error) && count < 2,
      staleTime: 30_000
    },
    mutations: {
      // Never automatic. A retried order is handled by the outbox, which knows
      // whether money was taken; a blind retry here does not.
      retry: false
    }
  }
})
